import { type LucideIcon } from 'lucide-react';
import * as React from 'react';

import { Tooltip } from '@/components/Tooltip';
import { cn } from '@/lib/utils';

export interface RadioCardOption<TValue extends string = string> {
  /** The value submitted when this card is selected. Must be unique within the group. */
  value: TValue;
  /** Primary label shown on the card. */
  label: string;
  /** Optional secondary description rendered beneath the label. */
  description?: string;
  /** Optional leading icon (lucide). */
  icon?: LucideIcon;
  /** When true the card is non-selectable and skipped by keyboard navigation. */
  disabled?: boolean;
  /** Explanatory note shown beneath a disabled card (e.g. why it is unavailable). */
  disabledNote?: string;
  /** Tooltip shown when a disabled card is hovered or focused. */
  disabledTooltip?: React.ReactNode;
}

export interface RadioCardGroupProps<TValue extends string = string> {
  /** Currently selected value (controlled). */
  value: TValue | null;
  /** Called with the newly selected value. */
  onChange: (value: TValue) => void;
  /** The cards to render. */
  options: RadioCardOption<TValue>[];
  /** Single- or two-column layout. Defaults to a single column. */
  columns?: 1 | 2;
  /** Accessible name for the radiogroup (maps to aria-label). */
  ariaLabel: string;
  /** Extra classes for the radiogroup container. */
  className?: string;
}

/**
 * RadioCardGroup — a single-choice control where each option is a full card
 * (icon + label + description) rather than a bare radio dot.
 *
 * Accessibility: implements true radiogroup semantics with a roving tabindex —
 * only the selected card (or the first enabled card when nothing is selected) is
 * in the tab order, and Arrow/Home/End move the selection between enabled cards.
 * Each card is `role="radio"` with `aria-checked`; the container is
 * `role="radiogroup"`. Disabled cards are skipped by keyboard navigation.
 *
 * Styling is Chariot-token-only (no hex/rgb/arbitrary values, no `dark:` —
 * tokens resolve per theme). Cards meet the 44px minimum target height.
 */
function RadioCardGroupInner<TValue extends string = string>(
  {
    value,
    onChange,
    options,
    columns = 1,
    ariaLabel,
    className,
  }: RadioCardGroupProps<TValue>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const enabledValues = options.filter(o => !o.disabled).map(o => o.value);

  // Roving tabindex: the focusable card is the selected one, else the first enabled one.
  const focusableValue =
    value !== null && enabledValues.includes(value)
      ? value
      : (enabledValues[0] ?? null);

  const moveSelection = (currentValue: TValue, direction: 1 | -1) => {
    if (enabledValues.length === 0) return;
    const currentIndex = enabledValues.indexOf(currentValue);
    const startIndex = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex =
      (startIndex + direction + enabledValues.length) % enabledValues.length;
    onChange(enabledValues[nextIndex]);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    optionValue: TValue
  ) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        moveSelection(optionValue, 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        moveSelection(optionValue, -1);
        break;
      case 'Home':
        event.preventDefault();
        if (enabledValues.length > 0) onChange(enabledValues[0]);
        break;
      case 'End':
        event.preventDefault();
        if (enabledValues.length > 0)
          onChange(enabledValues[enabledValues.length - 1]);
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'grid gap-3',
        columns === 2 ? 'sm:grid-cols-2' : 'grid-cols-1',
        className
      )}
    >
      {options.map(option => {
        const isSelected = option.value === value;
        const isDisabled = Boolean(option.disabled);
        const Icon = option.icon;
        const isFocusable = !isDisabled && option.value === focusableValue;

        const card = (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-disabled={isDisabled || undefined}
            disabled={isDisabled}
            tabIndex={isFocusable ? 0 : -1}
            onClick={() => !isDisabled && onChange(option.value)}
            onKeyDown={event =>
              !isDisabled && handleKeyDown(event, option.value)
            }
            className={cn(
              'flex min-h-11 w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors',
              'focus-visible:ring-chariot-brand-primary outline-hidden focus-visible:ring-2',
              isSelected
                ? 'border-chariot-brand-primary bg-chariot-background-secondary'
                : 'border-chariot-stroke',
              isDisabled
                ? 'cursor-not-allowed opacity-50'
                : 'hover:bg-chariot-background-secondary'
            )}
          >
            {Icon && (
              <Icon
                aria-hidden="true"
                className={cn(
                  'mt-0.5 size-5 shrink-0',
                  isSelected
                    ? 'text-chariot-brand-primary'
                    : 'text-chariot-text-secondary'
                )}
              />
            )}
            <span className="flex flex-col gap-0.5">
              <span className="text-chariot-text-primary text-sm font-medium">
                {option.label}
              </span>
              {option.description && (
                <span className="text-chariot-text-secondary text-xs">
                  {option.description}
                </span>
              )}
              {isDisabled && option.disabledNote && (
                <span className="text-chariot-text-secondary text-xs">
                  {option.disabledNote}
                </span>
              )}
            </span>
          </button>
        );

        if (isDisabled && option.disabledTooltip) {
          return (
            <Tooltip key={option.value} title={option.disabledTooltip}>
              <span
                className="block cursor-not-allowed [&>button]:pointer-events-none"
                aria-disabled="true"
                tabIndex={0}
              >
                {card}
              </span>
            </Tooltip>
          );
        }

        return card;
      })}
    </div>
  );
}

/**
 * forwardRef wrapper that preserves the generic `TValue` parameter (a plain
 * `forwardRef` erases generics). Cast keeps the call-site type inference.
 */
export const RadioCardGroup = React.forwardRef(RadioCardGroupInner) as <
  TValue extends string = string,
>(
  props: RadioCardGroupProps<TValue> & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  }
) => React.ReactElement;
